import React, { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Button from "components/Button";
import COLORS from "shared/constants/colors";
import { showLoader, hideLoader } from "actions/loader";
import { addCart, updateCartQuantity } from "actions/dtfCart";
import {
  MAX_UPLOAD_DESIGNS,
  MIN_QUANTITY,
  SHEET_WIDTH_IN,
  UI_PPI,
} from "pages/dtf-transfers/gang-sheet/data";
import {
  buildDesignsMetaFromObjects,
  buildGangSheetCartItem,
  getSheetTotalPrice,
  getSheetUnitPrice,
} from "pages/dtf-transfers/gang-sheet/helpers";
import {
  getDtfGangSheetFolder,
  isImageFile,
  openCloudinaryUploadWidget,
  uploadFileToCloudinary,
} from "pages/dtf-transfers/shared/cloudinary";
import GangCanvas, {
  addDesignToCanvas,
  deleteActiveObject,
  duplicateActiveObject,
  getGangDesignObjects,
  getSelectedDesignDpi,
  loadCanvasJson,
  runAutoArrange,
} from "pages/dtf-transfers/gang-sheet/GangCanvas";
import DesignLibraryPanel from "pages/dtf-transfers/gang-sheet/DesignLibraryPanel";
import {
  StepCard,
  Toolbar,
  ToolButton,
  WarningBanner,
  CartSuccessMessage,
  SheetPriceValue,
  HelpText,
  PropertiesPanel,
  PropertiesTitleRow,
  PropertiesEmptyState,
  PropertyGroup,
  PropertyRow,
  DpiIndicator,
  CheckoutControlsGroup,
  VerticalDivider,
  BuilderLayout,
} from "styles/pages/dtf-transfers/gang-sheet";

const designsFromMeta = (designsMeta = []) =>
  (designsMeta || [])
    .filter((design) => design?.secureUrl || design?.publicId)
    .map((design, index) => ({
      id: design.id || `${design.publicId || "design"}_${index}`,
      fileName: design.fileName,
      publicId: design.publicId,
      secureUrl: design.secureUrl,
      previewUrl: design.secureUrl,
      widthIn: design.width,
      heightIn: design.height,
    }));

const GangSheetBuilder = ({
  sheetWidth,
  sheetHeight,
  onClose,
  editItem = null,
}) => {
  const dispatch = useDispatch();
  const emailid = useSelector((state) => state?.auth?.profile?.emailid);
  const token = useSelector((state) => state?.auth?.profile?.token);
  const isCartLoading = useSelector((state) => state?.dtfCart?.loading);
  const isEditMode = Boolean(editItem?.cartId);

  const canvasRef = useRef(null);
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const historyLockRef = useRef(false);
  const restoreGenRef = useRef(0);

  const [designs, setDesigns] = useState(() =>
    designsFromMeta(editItem?.designsMeta),
  );
  const [isDragging, setIsDragging] = useState(false);

  const getInitialZoom = useCallback(() => {
    const canvasHeightPx = sheetHeight * UI_PPI;
    const targetHeight = 520;
    return Math.min(1, targetHeight / canvasHeightPx);
  }, [sheetHeight]);

  const [zoom, setZoom] = useState(getInitialZoom());
  const [selected, setSelected] = useState(null);
  const [hasOverlap, setHasOverlap] = useState(false);
  const [quantity, setQuantity] = useState(
    () =>
      Math.max(
        1,
        Number(editItem?.sizes?.[0]?.quantity || editItem?.quantity) ||
          MIN_QUANTITY,
      ),
  );
  const [artworkCheck, setArtworkCheck] = useState(() =>
    Boolean(editItem?.artworkCheck),
  );
  const [cartMessage, setCartMessage] = useState("");
  const [cartError, setCartError] = useState("");
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const unitPrice = getSheetUnitPrice(sheetWidth, sheetHeight);
  const totalPrice = getSheetTotalPrice(
    sheetWidth,
    sheetHeight,
    quantity,
    artworkCheck,
  );
  const selectedDpi = getSelectedDesignDpi(selected);

  const [widthInput, setWidthInput] = useState("");
  const [heightInput, setHeightInput] = useState("");

  const selectedWidthIn = selected
    ? ((selected.width * selected.scaleX) / UI_PPI).toFixed(1)
    : "";
  const selectedHeightIn = selected
    ? ((selected.height * selected.scaleY) / UI_PPI).toFixed(1)
    : "";

  React.useEffect(() => {
    if (selected) {
      setWidthInput(((selected.width * selected.scaleX) / UI_PPI).toFixed(1));
      setHeightInput(((selected.height * selected.scaleY) / UI_PPI).toFixed(1));
    } else {
      setWidthInput("");
      setHeightInput("");
    }
  }, [selected, selectedWidthIn, selectedHeightIn]);

  const onWidthInputChange = (e) => {
    const val = e.target.value;
    setWidthInput(val);
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && parsed > 0) {
      const canvas = canvasRef.current;
      if (!canvas || !selected) return;

      const maxAllowedWidthPx = sheetWidth * UI_PPI - 4.5 * 2;
      const targetWidthPx = Math.min(parsed * UI_PPI, maxAllowedWidthPx);
      const newScale = targetWidthPx / selected.width;

      selected.set({ scaleX: newScale, scaleY: newScale });
      selected.setCoords();
      canvas.requestRenderAll();
      syncDesignsFromCanvas(canvas);
    }
  };

  const onHeightInputChange = (e) => {
    const val = e.target.value;
    setHeightInput(val);
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && parsed > 0) {
      const canvas = canvasRef.current;
      if (!canvas || !selected) return;

      const maxAllowedHeightPx = sheetHeight * UI_PPI - 4.5 * 2;
      const targetHeightPx = Math.min(parsed * UI_PPI, maxAllowedHeightPx);
      const newScale = targetHeightPx / selected.height;

      selected.set({ scaleX: newScale, scaleY: newScale });
      selected.setCoords();
      canvas.requestRenderAll();
      syncDesignsFromCanvas(canvas);
    }
  };

  const onInputBlur = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    handleHistoryPush(
      JSON.stringify(canvas.toJSON(["designMeta", "isGangDesign"])),
    );
  };

  const syncHistoryFlags = () => {
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
  };

  const handleHistoryPush = useCallback((json) => {
    if (historyLockRef.current) return;
    const next = historyRef.current.slice(0, historyIndexRef.current + 1);
    next.push(json);
    if (next.length > 40) next.shift();
    historyRef.current = next;
    historyIndexRef.current = next.length - 1;
    syncHistoryFlags();
  }, []);

  const setHistoryLock = (locked) => {
    historyLockRef.current = locked;
  };

  const applyHistory = async (index) => {
    const canvas = canvasRef.current;
    const json = historyRef.current[index];
    if (!canvas || !json) return;
    historyIndexRef.current = index;
    await loadCanvasJson(canvas, json, { lockHistory: setHistoryLock });
    syncHistoryFlags();
  };

  const handleUndo = () => {
    if (historyIndexRef.current <= 0) return;
    applyHistory(historyIndexRef.current - 1);
  };

  const handleRedo = () => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    applyHistory(historyIndexRef.current + 1);
  };

  const registerDesign = (info) => {
    const design = {
      id: `${info.public_id}_${Date.now()}_${Math.random()}`,
      fileName: info.original_filename || info.public_id,
      publicId: info.public_id,
      secureUrl: info.secure_url,
      previewUrl: info.secure_url,
      width: info.width,
      height: info.height,
    };
    setDesigns((current) => {
      if (current.some((item) => item.publicId === design.publicId)) {
        return current;
      }
      return [...current, design].slice(0, MAX_UPLOAD_DESIGNS);
    });
    return design;
  };

  const syncDesignsFromCanvas = (canvas) => {
    if (!canvas) return;
    const objects = getGangDesignObjects(canvas);
    const synchedDesigns = objects.map((obj) => {
      const widthIn = (obj.width * obj.scaleX) / UI_PPI;
      const heightIn = (obj.height * obj.scaleY) / UI_PPI;
      const secureUrl =
        obj.designMeta?.secureUrl ||
        (typeof obj.getSrc === "function" ? obj.getSrc() : obj.src) ||
        null;
      return {
        id:
          obj.designMeta?.id ||
          `${obj.designMeta?.publicId || "design"}_${Date.now()}_${Math.random()}`,
        fileName: obj.designMeta?.fileName,
        publicId: obj.designMeta?.publicId,
        secureUrl,
        previewUrl: secureUrl,
        widthIn,
        heightIn,
      };
    });
    setDesigns(synchedDesigns);
  };

  const placeDesign = async (design) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const matchingObj = canvas.getObjects().find(
      (obj) => obj.isGangDesign && obj.designMeta?.id === design.id,
    );
    if (matchingObj) {
      canvas.setActiveObject(matchingObj);
      canvas.requestRenderAll();
      return;
    }

    await addDesignToCanvas(
      canvas,
      {
        url: design.secureUrl,
        fileName: design.fileName,
        publicId: design.publicId,
        secureUrl: design.secureUrl,
        width: design.width,
        height: design.height,
        id: design.id,
      },
      sheetWidth || SHEET_WIDTH_IN,
    );
    handleHistoryPush(
      JSON.stringify(canvas.toJSON(["designMeta", "isGangDesign"])),
    );
    syncDesignsFromCanvas(canvas);
  };

  const handleCanvasReady = useCallback(
    async (canvas) => {
      if (!canvas || !editItem?.canvasData) return;
      // Already restored onto this canvas instance
      if (getGangDesignObjects(canvas).length > 0) return;

      const gen = ++restoreGenRef.current;

      try {
        await loadCanvasJson(canvas, editItem.canvasData, {
          lockHistory: setHistoryLock,
        });
        // Ignore stale restore after React remounts/disposes the canvas
        if (gen !== restoreGenRef.current || canvasRef.current !== canvas) {
          return;
        }
        handleHistoryPush(
          JSON.stringify(canvas.toJSON(["designMeta", "isGangDesign"])),
        );
        syncDesignsFromCanvas(canvas);
      } catch (error) {
        if (gen !== restoreGenRef.current) return;
        console.error("Failed to restore gang sheet canvas:", error);
        setCartError("Could not restore the saved gang sheet layout.");
      }
    },
    [editItem, handleHistoryPush],
  );

  const openUploadWidget = () => {
    if (!emailid) return;
    openCloudinaryUploadWidget(
      emailid,
      async (info) => {
        const design = registerDesign(info);
        await placeDesign(design);
      },
      {
        folder: getDtfGangSheetFolder(emailid),
        clientAllowedFormats: ["png", "jpeg", "jpg", "svg"],
      },
    );
  };

  const uploadDroppedFiles = async (fileList) => {
    if (!emailid) return;
    const files = Array.from(fileList || [])
      .filter(isImageFile)
      .slice(0, MAX_UPLOAD_DESIGNS);
    if (!files.length) return;

    try {
      dispatch(showLoader());
      for (const file of files) {
        const info = await uploadFileToCloudinary(
          file,
          emailid,
          getDtfGangSheetFolder(emailid),
        );
        const design = registerDesign(info);
        await placeDesign(design);
      }
    } catch (error) {
      console.error(error);
      setCartError("Upload failed. Please try again.");
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleAddToCart = async () => {
    const canvas = canvasRef.current;

    if (!emailid || !token) {
      setCartError("Please sign in to add items to your cart.");
      setCartMessage("");
      return;
    }

    if (!canvas) {
      setCartError("Sheet canvas is not ready. Refresh and try again.");
      setCartMessage("");
      return;
    }

    const designsOnSheet = getGangDesignObjects(canvas);
    if (!designsOnSheet.length) {
      setCartError("Add at least one design to the sheet.");
      setCartMessage("");
      return;
    }

    if (hasOverlap) {
      setCartError("Please resolve overlapping designs before adding to cart.");
      setCartMessage("");
      return;
    }

    setCartError("");
    setCartMessage("");
    dispatch(showLoader());

    try {
      const designsMeta = buildDesignsMetaFromObjects(designsOnSheet);
      const previewUrl =
        designsMeta.find((design) => design.secureUrl)?.secureUrl ||
        editItem?.previewUrl ||
        null;
      const publicId =
        designsMeta.find((design) => design.publicId)?.publicId ||
        editItem?.publicId ||
        null;

      if (!previewUrl) {
        setCartError("Could not prepare gang sheet preview for cart.");
        return;
      }

      const payload = buildGangSheetCartItem({
        mode: "create",
        sheetWidth,
        sheetHeight,
        quantity,
        fileName:
          editItem?.fileName || `Gang Sheet ${sheetWidth}x${sheetHeight}`,
        previewUrl,
        secureUrl: previewUrl,
        publicId,
        artworkCheck,
        designsMeta,
        canvasData: JSON.stringify(
          canvas.toJSON(["designMeta", "isGangDesign"]),
        ),
      });

      const result = isEditMode
        ? await dispatch(
            updateCartQuantity(emailid, token, {
              cartId: editItem.cartId,
              item: payload,
            }),
          )
        : await dispatch(addCart(emailid, token, payload));

      if (result?.error) {
        setCartError(
          result.message ||
            (isEditMode
              ? "Failed to update cart item."
              : "Failed to add item to cart."),
        );
        return;
      }

      setCartMessage(
        isEditMode
          ? "Cart updated successfully."
          : "Added to cart successfully.",
      );
      window.setTimeout(() => {
        setCartMessage("");
        if (onClose) onClose();
      }, 2000);
    } catch (error) {
      console.error("Gang sheet cart API failed:", error);
      setCartError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          (isEditMode
            ? "Could not update gang sheet in cart."
            : "Could not add gang sheet to cart."),
      );
    } finally {
      dispatch(hideLoader());
    }
  };

  const hasSelection = Boolean(selected?.isGangDesign || selected?.designMeta);

  return (
    <StepCard>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "18px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <span
            style={{
              fontSize: "16px",
              fontFamily: "Inter700",
              color: "#1e293b",
              display: "block",
            }}
          >
            Design Workspace
          </span>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Drag and drop images onto the canvas to arrange your gang sheet.
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "8px",
          }}
        >
          <CheckoutControlsGroup>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#475569",
                }}
              >
                Qty:
              </span>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(event) =>
                  setQuantity(
                    Math.max(1, parseInt(event.target.value, 10) || 1),
                  )
                }
                style={{
                  width: "54px",
                  padding: "6px 4px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "14px",
                  fontWeight: "700",
                  textAlign: "center",
                  outline: "none",
                  background: "#ffffff",
                }}
              />
            </div>

            <VerticalDivider />

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "1px",
                  lineHeight: "1",
                }}
              >
                Total Price
              </div>
              <div
                style={{ display: "flex", alignItems: "baseline", gap: "6px" }}
              >
                <SheetPriceValue
                  style={{
                    fontSize: "20px",
                    fontFamily: "Inter700",
                    color: COLORS.BLUE,
                    lineHeight: "1",
                  }}
                >
                  ${totalPrice.toFixed(2)}
                </SheetPriceValue>
                <span
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    fontWeight: "500",
                  }}
                >
                  (${unitPrice.toFixed(2)} / sheet)
                </span>
              </div>
            </div>

            <VerticalDivider />

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                color: "#475569",
                cursor: "pointer",
                fontFamily: "Inter600",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={artworkCheck}
                onChange={(event) => setArtworkCheck(event.target.checked)}
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  accentColor: COLORS.BLUE,
                }}
              />
              Artwork check (+$10.00)
            </label>

            <VerticalDivider />

            <Button
              title={isEditMode ? "Update Cart" : "Add to Cart"}
              startIcon={<ShoppingCartOutlinedIcon />}
              disabled={isCartLoading}
              onClick={handleAddToCart}
              containerStyle={{
                background: COLORS.BLUE,
                color: COLORS.WHITE,
                textTransform: "none",
                borderRadius: "6px",
                padding: "0 18px",
                fontWeight: "700",
                height: "36px",
                boxShadow: "0 2px 4px rgba(21, 148, 219, 0.2)",
              }}
            />
          </CheckoutControlsGroup>
          {cartMessage ? (
            <CartSuccessMessage style={{ margin: 0 }}>
              {cartMessage}
            </CartSuccessMessage>
          ) : null}
          {cartError ? (
            <CartSuccessMessage $error style={{ margin: 0 }}>
              {cartError}
            </CartSuccessMessage>
          ) : null}
        </div>
      </div>
      <BuilderLayout>
        <DesignLibraryPanel
          designs={designs}
          isDragging={isDragging}
          onBrowse={openUploadWidget}
          onDropFiles={uploadDroppedFiles}
          onDragState={setIsDragging}
          onAddDesign={placeDesign}
        />
        <div>
          <Toolbar>
            <ToolButton type="button" onClick={handleUndo} disabled={!canUndo}>
              <UndoIcon /> Undo
            </ToolButton>
            <ToolButton type="button" onClick={handleRedo} disabled={!canRedo}>
              <RedoIcon /> Redo
            </ToolButton>
            <ToolButton
              type="button"
              onClick={() => setZoom((value) => Math.max(0.01, value / 1.25))}
            >
              <ZoomOutIcon /> Zoom out
            </ToolButton>
            <span
              style={{
                fontSize: "13px",
                fontFamily: "Inter600",
                color: "#64748b",
                alignSelf: "center",
                minWidth: "40px",
                textAlign: "center",
              }}
            >
              {Math.round(zoom * 100)}%
            </span>
            <ToolButton
              type="button"
              onClick={() => setZoom((value) => Math.min(4, value * 1.25))}
            >
              <ZoomInIcon /> Zoom in
            </ToolButton>
            <ToolButton
              type="button"
              onClick={() => {
                runAutoArrange(canvasRef.current, sheetWidth, sheetHeight);
                const canvas = canvasRef.current;
                if (canvas) {
                  handleHistoryPush(
                    JSON.stringify(
                      canvas.toJSON(["designMeta", "isGangDesign"]),
                    ),
                  );
                }
              }}
            >
              <AutoAwesomeMosaicIcon /> Auto arrange
            </ToolButton>
            <ToolButton
              type="button"
              disabled={!hasSelection}
              onClick={async () => {
                const cloned = await duplicateActiveObject(canvasRef.current);
                const canvas = canvasRef.current;
                if (canvas) {
                  if (cloned && cloned.designMeta) {
                    cloned.designMeta.id = `${cloned.designMeta.publicId}_${Date.now()}_${Math.random()}`;
                  }
                  handleHistoryPush(
                    JSON.stringify(
                      canvas.toJSON(["designMeta", "isGangDesign"]),
                    ),
                  );
                  syncDesignsFromCanvas(canvas);
                }
              }}
            >
              <ContentCopyIcon /> Duplicate
            </ToolButton>
            <ToolButton
              type="button"
              disabled={!hasSelection}
              onClick={() => {
                const canvas = canvasRef.current;
                if (!canvas) return;
                deleteActiveObject(canvas);
                handleHistoryPush(
                  JSON.stringify(canvas.toJSON(["designMeta", "isGangDesign"])),
                );
                syncDesignsFromCanvas(canvas);
              }}
            >
              <DeleteOutlineIcon /> Delete
            </ToolButton>
            {selectedDpi != null ? (
              <HelpText style={{ margin: "0 0 0 12px", alignSelf: "center" }}>
                Selected ≈ {selectedDpi} DPI
              </HelpText>
            ) : null}
          </Toolbar>

          <GangCanvas
            sheetWidth={sheetWidth}
            sheetHeight={sheetHeight}
            zoom={zoom}
            canvasRef={canvasRef}
            onSelectionChange={setSelected}
            onOverlapChange={setHasOverlap}
            onHistoryPush={handleHistoryPush}
            onObjectsChange={syncDesignsFromCanvas}
            onReady={handleCanvasReady}
          />

          {hasOverlap ? (
            <WarningBanner>
              Some designs overlap. Tight nesting is OK, but check your layout
              before ordering.
            </WarningBanner>
          ) : null}
        </div>

        <PropertiesPanel>
          <PropertiesTitleRow>
            <h3>Object Properties</h3>
            {selected ? (
              <button
                type="button"
                onClick={() => {
                  const canvas = canvasRef.current;
                  if (canvas) {
                    canvas.discardActiveObject();
                    canvas.requestRenderAll();
                  }
                  setSelected(null);
                }}
              >
                ✕
              </button>
            ) : null}
          </PropertiesTitleRow>

          {selected ? (
            <>
              <PropertyGroup>
                <label htmlFor="prop-name">Name</label>
                <input
                  id="prop-name"
                  type="text"
                  value={selected.designMeta?.fileName || "Design"}
                  disabled
                />
              </PropertyGroup>

              <PropertyRow>
                <PropertyGroup>
                  <label htmlFor="prop-width">Width (in)</label>
                  <input
                    id="prop-width"
                    type="text"
                    value={widthInput}
                    onChange={onWidthInputChange}
                    onBlur={onInputBlur}
                  />
                </PropertyGroup>

                <PropertyGroup>
                  <label htmlFor="prop-height">Height (in)</label>
                  <input
                    id="prop-height"
                    type="text"
                    value={heightInput}
                    onChange={onHeightInputChange}
                    onBlur={onInputBlur}
                  />
                </PropertyGroup>
              </PropertyRow>

              {selectedDpi != null ? (
                <DpiIndicator>
                  <span className="dpi-label">Resolution Details</span>
                  <div
                    className={`dpi-badge ${
                      selectedDpi < 255
                        ? "low"
                        : selectedDpi <= 299
                          ? "ok"
                          : "good"
                    }`}
                  >
                    <span>{selectedDpi} DPI</span>
                    <span>
                      {selectedDpi < 255
                        ? "Image Quality is low"
                        : selectedDpi <= 299
                          ? "Image Quality is ok"
                          : "Image Quality is good"}
                    </span>
                  </div>
                </DpiIndicator>
              ) : null}
            </>
          ) : (
            <PropertiesEmptyState>
              Select a design on the sheet to edit size and resolution.
            </PropertiesEmptyState>
          )}
        </PropertiesPanel>
      </BuilderLayout>
    </StepCard>
  );
};

export default GangSheetBuilder;
